/**
 * React bindings for the gamepad stream.
 *
 * Direction buttons are handed to the spatial navigation library as synthetic key events, which
 * is how one input source drives both the gamepad and the keyboard path without two code paths.
 */

import * as React from 'react';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import {
  onGamepad,
  onGamepadRelease,
  onGamepadScroll,
  type GamepadButton,
} from '../../gamepad/input.js';
import { revealNextAs } from './reveal.js';

/**
 * Runs `handler` while `enabled` and the given button is pressed.
 *
 * A held direction repeats and so calls the handler again; every other button fires once per
 * press, because nothing bound to A or B or X wants to happen forty times for one hold.
 */
export function useGamepadButton(button: GamepadButton, handler: () => void, enabled = true): void {
  const ref = React.useRef(handler);
  ref.current = handler;

  React.useEffect(() => {
    if (!enabled) return;
    return onGamepad((event) => {
      if (event.button === button) ref.current();
    });
  }, [button, enabled]);
}

/** The key each gamepad button stands in for. */
const KEYS: Partial<Record<GamepadButton, string>> = {
  up: 'ArrowUp',
  down: 'ArrowDown',
  left: 'ArrowLeft',
  right: 'ArrowRight',
  a: 'Enter',
};

/**
 * Wires the direction buttons and A to the spatial navigation library.
 *
 * The library listens for key events, so the gamepad is turned into the keys it already
 * understands. Mounted once, by the app shell.
 *
 * One event per press, dispatched on `window` and nowhere else. The library binds its listener
 * to `window`, so sending the same press to the focused node as well — which bubbles up to
 * `window` — handed it two of everything: every direction moved focus two tiles, and every A
 * opened the game twice and put two entries in the history, so B only looked like it had
 * stopped working. That was the whole of why a stick felt like it was skidding.
 *
 * The matching release matters too. The library counts keys down and hands the count to
 * `onEnterPress`, and clears it on key up; a press with no release leaves that count climbing
 * for as long as the app is open.
 */
export function useGamepadNavigation(): void {
  React.useEffect(() => {
    const send = (type: 'keydown' | 'keyup', key: string) => {
      window.dispatchEvent(new KeyboardEvent(type, { key, bubbles: true, cancelable: true }));
    };

    const offPress = onGamepad((event) => {
      const key = KEYS[event.button];
      if (key) send('keydown', key);
    });
    const offRelease = onGamepadRelease((event) => {
      const key = KEYS[event.button];
      if (key) send('keyup', key);
    });

    return () => {
      offPress();
      offRelease();
    };
  }, []);
}

/**
 * The triggers page through a long screen (section 3.3).
 *
 * A stick walks one tile at a time, which is the right speed for choosing and the wrong speed
 * for getting past forty of them. LT and RT move a screenful, in whichever scroll area the
 * focused element is in, so a long list has a way through that is not forty presses.
 *
 * A page takes focus with it. Scrolling the area and leaving the ring where it was is worse than
 * not paging at all: the next press of a direction moves focus by one from wherever it still is,
 * and the screen snaps straight back to it, so the jump undoes itself and nothing has happened.
 */
export function useGamepadPaging(): void {
  React.useEffect(() => {
    return onGamepad((event) => {
      if (event.button !== 'lt' && event.button !== 'rt') return;
      const scroller = scrollerInPlay();
      if (!scroller) return;
      const down = event.button === 'rt';
      // Just under a screenful, so the line that was at the edge is still on screen after the
      // jump and nobody has to work out what they skipped.
      const page = scroller.clientHeight * 0.9;

      const landing = focusAPageAway(scroller, down ? page : -page);
      if (landing) {
        // The landing element brings the screen with it, so there is no second scroll here:
        // two at once would fight, and the ring would arrive somewhere the page was not. It
        // comes to rest at the leading edge, so what is in front of it is what has not been read.
        revealNextAs(down ? 'start' : 'end');
        setFocus(landing);
        return;
      }
      scroller.scrollBy({ top: down ? page : -page, behavior: 'smooth' });
    });
  }, []);
}

/**
 * The element a page in this direction should land on.
 *
 * A page down should feel like the same column, further along — the way a page down in any list
 * does. So the candidates that overlap the focused element horizontally are preferred, and among
 * those the one nearest where the eye is about to be. Only when nothing shares the column does it
 * fall back to plain distance, with sideways travel weighted up so it still resists wandering
 * off across the screen.
 *
 * Returns null when there is nothing to land on, which is the case a plain scroll is for: an
 * audit table or a storage report has no focusable rows and still has to be readable.
 */
function focusAPageAway(scroller: Element, distance: number): string | null {
  const from = document.querySelector<HTMLElement>('[data-focused="true"]');
  const candidates = [...scroller.querySelectorAll<HTMLElement>('[data-focus-key]')].filter(
    (node) => !node.classList.contains('gb-focusable--disabled') && node.offsetParent !== null,
  );
  if (candidates.length === 0) return null;

  const start = from?.getBoundingClientRect();
  // With nothing focused yet, a page is measured from the edge of the screen it would come from.
  const originY = start ? start.top + start.height / 2 : distance > 0 ? 0 : window.innerHeight;
  const originX = start ? start.left + start.width / 2 : 0;
  const targetY = originY + distance;

  let best: { key: string; score: number } | null = null;
  for (const node of candidates) {
    if (node === from) continue;
    const key = node.dataset.focusKey;
    if (!key) continue;
    const box = node.getBoundingClientRect();
    const centreY = box.top + box.height / 2;
    // Only what lies the way the page is going, so a page down never lands above where it began.
    if (distance > 0 ? centreY <= originY : centreY >= originY) continue;

    const sharesColumn = start ? box.right > start.left && box.left < start.right : true;
    const centreX = box.left + box.width / 2;
    const score =
      Math.abs(centreY - targetY) + (sharesColumn ? 0 : 2000 + Math.abs(centreX - originX));
    if (!best || score < best.score) best = { key, score };
  }

  return best?.key ?? null;
}

/** Right stick scrolls whichever scroll area contains the focused element. */
export function useGamepadScrolling(): void {
  React.useEffect(() => {
    return onGamepadScroll((delta) => {
      scrollerInPlay()?.scrollBy({ top: delta, behavior: 'auto' });
    });
  }, []);
}

/**
 * The scroll area a stick or a trigger should move.
 *
 * Whatever holds the focused element, and the screen itself when nothing is focused — a table
 * of audit rows has nothing to focus and still has to be readable from a controller. The
 * document is last and is almost never it: the shell is a fixed grid, and the part that
 * scrolls is the main area inside it.
 */
function scrollerInPlay(): Element | null {
  const focused = document.querySelector('[data-focused="true"]');
  return (
    findScrollParent(focused as HTMLElement | null) ??
    document.querySelector('.gb-os__main') ??
    document.scrollingElement
  );
}

function findScrollParent(node: HTMLElement | null): Element | null {
  let current: HTMLElement | null = node;
  while (current) {
    const style = getComputedStyle(current);
    if (/(auto|scroll)/.test(style.overflowY) && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}
